/**
 * TikTok 商品描述富文本编辑器（Create Product description 支持 HTML）。
 * 轻量 contentEditable，工具栏：撤销/重做/清格式/插图/标题/B/I/U/列表/全屏。
 * 插图支持本地文件或图片空间（转 TikTok 图址后写入 description）。
 */
import { fixedUi } from '@/i18n/fixedUi';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Bold,
  Eraser,
  Heading2,
  Image as ImageIcon,
  Italic,
  List,
  ListOrdered,
  Maximize2,
  Minimize2,
  Redo2,
  Underline,
  Undo2,
} from 'lucide-react';
import { tiktokPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import TiktokUploadSourceMenu from '@/tiktok-platform/components/TiktokUploadSourceMenu';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import { imageSpaceRecordToUploadFile } from '@/tiktok-platform/utils/tiktokImageSpacePick';
import { TIKTOK_DESCRIPTION_MAX_CHARS } from '@/tiktok-platform/utils/tiktokPublishFormLimits';
import {
  prepareTiktokDescriptionImage,
  TIKTOK_DESCRIPTION_IMAGE_USE_CASE,
  TIKTOK_PRODUCT_IMAGE_ACCEPT,
} from '@/tiktok-platform/utils/tiktokProductImage';
import '@/styles/ipe/workbench/IpeModalUnified.css';

function normalizeHtml(html) {
  const raw = String(html || '').trim();
  if (!raw || raw === '<br>' || raw === '<div><br></div>') return '';
  return raw;
}

function runCommand(command, value = null) {
  try {
    document.execCommand(command, false, value);
  } catch {
    // ignore unsupported command
  }
}

function escapeAttr(value) {
  return String(value || '').replace(/"/g, '&quot;');
}

function ToolbarButton({ title, onClick, disabled, active, buttonRef, children }) {
  return (
    <button
      ref={buttonRef}
      type="button"
      className={`tko-rich-editor__btn${active ? ' is-active' : ''}`}
      title={title}
      aria-label={title}
      disabled={disabled}
      onMouseDown={(e) => {
        // 避免点击工具栏时编辑区失焦，导致选区丢失
        e.preventDefault();
        onClick?.();
      }}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <span className="tko-rich-editor__divider" aria-hidden="true" />;
}

/**
 * @param {object} props
 * @param {string} props.value HTML
 * @param {(html: string) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {string|number} [props.shopId] 插入图片时需店铺转图
 * @param {boolean} [props.disabled]
 * @param {number} [props.maxLength]
 */
export default function TiktokDescriptionRichEditor({
  value = '',
  onChange,
  placeholder = fixedUi('TikTok 商品描述须为英文，可含简单 HTML。'),
  shopId,
  disabled = false,
  maxLength = TIKTOK_DESCRIPTION_MAX_CHARS,
}) {
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const imageBtnRef = useRef(null);
  const imageSpaceRowsRef = useRef([]);
  const lastEmittedRef = useRef(normalizeHtml(value));
  const [fullscreen, setFullscreen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySelection, setLibrarySelection] = useState([]);
  const [charCount, setCharCount] = useState(() => String(value || '').length);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = normalizeHtml(el.innerHTML);
    lastEmittedRef.current = html;
    setCharCount(html.length);
    if (html !== normalizeHtml(value)) {
      onChange?.(html);
    }
  }, [onChange, value]);

  // 外部 value 变化时回写（避免覆盖正在编辑的选区）
  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = normalizeHtml(value);
    if (next === lastEmittedRef.current && normalizeHtml(el.innerHTML) === next) return;
    if (document.activeElement === el) return;
    el.innerHTML = next || '';
    lastEmittedRef.current = next;
    setCharCount(next.length);
  }, [value]);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const apply = (command, arg = null) => {
    if (disabled) return;
    focusEditor();
    runCommand(command, arg);
    emitChange();
  };

  const insertHeading = () => {
    if (disabled) return;
    focusEditor();
    runCommand('formatBlock', 'h2');
    emitChange();
  };

  const insertImageUrl = (url) => {
    const src = String(url || '').trim();
    if (!src) return;
    focusEditor();
    // Create Product：描述内图须为 TikTok 图址；附带宽高属性更稳妥
    runCommand(
      'insertHTML',
      `<img src="${escapeAttr(src)}" width="800" height="800" alt="" />`,
    );
    emitChange();
  };

  const uploadDescriptionImageFile = async (file) => {
    // DESCRIPTION_IMAGE：通用 100～20000；先钳尺寸再上传，避免 12038014
    const prepared = await prepareTiktokDescriptionImage(file);
    const res = await tiktokPlatformAPI.uploadProductPic(shopId, prepared, {
      imageType: 7,
      imageBizType: 0,
      useCase: TIKTOK_DESCRIPTION_IMAGE_USE_CASE,
      fromImageSpace: true,
    });
    const url = String(
      res?.url || res?.tiktokUrl || res?.imageUrl || '',
    ).trim();
    if (!url) throw new Error(fixedUi('图片上传成功但未返回可用地址'));
    return url;
  };

  const insertImageFromFile = async (file) => {
    if (!file || disabled) return;
    if (!shopId) {
      showToast(fixedUi("请先选择店铺后再插入描述图片"), 'warning');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadDescriptionImageFile(file);
      insertImageUrl(url);
      showToast(fixedUi("描述图片已插入"), 'success');
    } catch (e) {
      showToast(e?.message || fixedUi('描述图片上传失败'), 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const rememberImageSpaceRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  }, []);

  const handleLibraryClose = () => {
    setLibrarySelection([]);
    setLibraryOpen(false);
  };

  const handleLibraryToggle = useCallback((imageId) => {
    const id = String(imageId);
    startTransition(() => {
      setLibrarySelection((prev) => (
        prev.some((x) => String(x) === id) ? [] : [id]
      ));
    });
  }, []);

  const insertImageFromSpace = async (records) => {
    const picked = (Array.isArray(records) ? records : []).filter(Boolean);
    if (!picked.length) return;
    if (!shopId) {
      showToast(fixedUi("请先选择店铺后再插入描述图片"), 'warning');
      return;
    }
    setUploading(true);
    try {
      // 描述可插多张，但单次确认只处理已选（最多按选择顺序）
      for (const image of picked) {
        const rawFile = await imageSpaceRecordToUploadFile(image);
        const url = await uploadDescriptionImageFile(rawFile);
        insertImageUrl(url);
      }
      showToast(picked.length > 1 ? `${fixedUi('已插入')} ${picked.length} ${fixedUi('张描述图片')}` : fixedUi('描述图片已插入'), 'success');
    } catch (e) {
      showToast(e?.message || fixedUi('从图片空间插入失败'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleLibraryConfirm = async (positionArray) => {
    const ids = (positionArray || []).filter((x) => x != null && x !== '');
    if (ids.length === 0) {
      showToast(fixedUi("请至少选择一张图片"), 'info');
      return;
    }
    const records = ids
      .map((id) => imageSpaceRowsRef.current.find((row) => String(row?.id) === String(id)))
      .filter(Boolean);
    if (records.length === 0) {
      showToast(fixedUi("所选图片无效或已失效，请重试"), 'warning');
      return;
    }
    handleLibraryClose();
    await insertImageFromSpace(records);
  };

  const openImageMenu = () => {
    if (disabled || uploading) return;
    if (!shopId) {
      showToast(fixedUi("请先选择店铺后再插入描述图片"), 'warning');
      return;
    }
    setUploadMenuOpen(true);
  };

  const overLimit = charCount > maxLength;
  const isEmpty = charCount === 0;
  const imageBusy = disabled || uploading || !shopId;

  return (
    <div className={`tko-rich-editor${fullscreen ? ' is-fullscreen' : ''}${disabled ? ' is-disabled' : ''}`}>
      <div className="tko-rich-editor__toolbar" role="toolbar" aria-label={fixedUi("描述编辑工具栏")}>
        <ToolbarButton title={fixedUi("撤销")} disabled={disabled} onClick={() => apply('undo')}>
          <Undo2 size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton title={fixedUi("重做")} disabled={disabled} onClick={() => apply('redo')}>
          <Redo2 size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton title={fixedUi("清除格式")} disabled={disabled} onClick={() => apply('removeFormat')}>
          <Eraser size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton
          title={fixedUi("插入图片（本地 / 图片空间）")}
          disabled={imageBusy}
          buttonRef={imageBtnRef}
          onClick={openImageMenu}
        >
          <ImageIcon size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton title={fixedUi("标题")} disabled={disabled} onClick={insertHeading}>
          <Heading2 size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton title={fixedUi("加粗")} disabled={disabled} onClick={() => apply('bold')}>
          <Bold size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton title={fixedUi("斜体")} disabled={disabled} onClick={() => apply('italic')}>
          <Italic size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton title={fixedUi("下划线")} disabled={disabled} onClick={() => apply('underline')}>
          <Underline size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton title={fixedUi("无序列表")} disabled={disabled} onClick={() => apply('insertUnorderedList')}>
          <List size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton title={fixedUi("有序列表")} disabled={disabled} onClick={() => apply('insertOrderedList')}>
          <ListOrdered size={16} strokeWidth={1.75} />
        </ToolbarButton>
        <span className="tko-rich-editor__toolbar-spacer" />
        <ToolbarButton
          title={fullscreen ? fixedUi('退出全屏') : fixedUi('全屏编辑')}
          disabled={disabled}
          onClick={() => setFullscreen((v) => !v)}
        >
          {fullscreen
            ? <Minimize2 size={16} strokeWidth={1.75} />
            : <Maximize2 size={16} strokeWidth={1.75} />}
        </ToolbarButton>
      </div>

      <div
        ref={editorRef}
        className={`tko-rich-editor__body${isEmpty ? ' is-empty' : ''}`}
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={(e) => {
          e.preventDefault();
          const html = e.clipboardData?.getData('text/html');
          const text = e.clipboardData?.getData('text/plain') || '';
          if (html) {
            runCommand('insertHTML', html);
          } else {
            runCommand('insertText', text);
          }
          emitChange();
        }}
      />

      <input
        ref={fileRef}
        type="file"
        accept={TIKTOK_PRODUCT_IMAGE_ACCEPT}
        hidden
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files || []).filter(Boolean);
          if (!files.length) return;
          void (async () => {
            for (const file of files) {
              await insertImageFromFile(file);
            }
          })();
        }}
      />

      <TiktokUploadSourceMenu
        open={uploadMenuOpen}
        anchorRef={imageBtnRef}
        onClose={() => setUploadMenuOpen(false)}
        disabled={uploading}
        onPickLocal={() => fileRef.current?.click()}
        onPickImageSpace={() => setLibraryOpen(true)}
      />
      <LibraryModalContent
        isOpen={libraryOpen}
        onClose={handleLibraryClose}
        images={[]}
        selection={librarySelection}
        onToggle={handleLibraryToggle}
        maxSelection={10}
        onConfirm={handleLibraryConfirm}
        onImagesAppended={rememberImageSpaceRows}
        uploadOriginSource="upload"
        showToast={showToast}
        deferUsageBump
        selectAllLabel={fixedUi("全选已加载")}
        closeOnBackdropClick={false}
      />

      <div className="tko-rich-editor__footer">
        <span className={`tko-publish__counter${overLimit ? ' is-over' : ''}`}>
          {charCount}/{maxLength}
          {uploading ? ' · 图片处理中…' : ''}
        </span>
      </div>
    </div>
  );
}
